/**
 * Redesigned Design-tab left nav. Three content groups — Build (the Build
 * Plan card with the SINGLE status chip), Design Docs, and Screens — plus a
 * pinned one-line footer. Pure presentation: all state arrives derived
 * (design-status.ts) and every interaction is a callback.
 */
import type { ReactElement, ReactNode } from 'react';
import { Check, FileCheck2 } from 'lucide-react';
import { kindShortLabel } from './level-defaults';
import type { WorldGrouping } from './ProgressionEditor';
import type { LevelEntry, Screen, SectionMeta } from '@volley/crucible-ipc-types';
import type {
  BuildPlanChip,
  NavFooterLine,
  ScreensGenerateState,
  StatusTone,
} from './design-status';

const CHIP_TONE_CLASS: Record<StatusTone, string> = {
  gray: 'border-white/15 bg-white/[0.06] text-crucible-muted',
  blue: 'border-crucible-accent/40 bg-crucible-accent/15 text-crucible-accent-hover',
  amber: 'border-amber-300/30 bg-amber-300/10 text-amber-200',
  green: 'border-emerald-300/30 bg-emerald-300/10 text-emerald-300',
};

const FOOTER_TONE_CLASS: Record<StatusTone, string> = {
  gray: 'text-crucible-muted',
  blue: 'text-crucible-accent-hover',
  amber: 'text-amber-200',
  green: 'text-crucible-muted',
};

function navItemClass(active: boolean): string {
  return `flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm ${
    active
      ? 'bg-crucible-accent/20 text-crucible-fg'
      : 'text-crucible-muted hover:bg-crucible-bg-elevated'
  }`;
}

function GroupHeading({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <h2 className="text-[11px] font-semibold uppercase tracking-wide text-crucible-muted">
      {children}
    </h2>
  );
}

/** The 6px amber "edited since last build" dot. */
function EditedDot(): ReactElement {
  return (
    <span
      title="Edited since the last build"
      data-testid="design-edited-dot"
      className="size-1.5 shrink-0 rounded-full bg-amber-300"
    />
  );
}

export interface DesignNavProps {
  /** Build Plan surface available (the host passed a buildPlan). */
  hasBuildPlan: boolean;
  buildPlanActive: boolean;
  chip: BuildPlanChip;
  footer: NavFooterLine;
  sections: SectionMeta[];
  activeFilename: string | null;
  /** Section/screen ids edited since the last build snapshot (amber dots). */
  changedSectionIds: string[];
  changedScreenIds: string[];
  screens: Screen[];
  activeScreenId: string | null;
  generate: ScreensGenerateState;
  genError: string | null;
  /** Levels (procgen level design) — sibling group to Screens. */
  levels: LevelEntry[];
  activeLevelId: string | null;
  levelsGenerating: boolean;
  levelGenError: string | null;
  /** Non-fatal outcome note (e.g. "created with 2 issues") — amber, NOT error red. */
  levelGenNote: string | null;
  /** Live batch progress ("Generating 3/10 — Neon Arcade"). */
  levelGenProgress?: string | null;
  /** 3Cs feel profile (Epic B) — surfaced in Levels. */
  threeCsAuthored: boolean;
  activeThreeCs: boolean;
  /** Progression (design §16) — campaign structure entry + per-level badges. */
  progressionAuthored: boolean;
  activeProgression: boolean;
  levelBadges: Record<string, string>;
  /** World grouping derived from a `sets`-mode progression (design §16). When present the
   *  Levels list renders one header per world with its levels beneath, instead of the flat
   *  badge-sorted list. Null in every other mode. */
  worldGroups?: WorldGrouping | null;
  /** Roster health under the current 3Cs + Content (design §18/§19): levels that now fail
   *  validation, and levels referencing missing Content. 0 = hidden. */
  levelHealth?: { errorLevels: number; orphanLevels: number };
  /** Level ids reachable from neither the progression nor any screen — flagged "orphaned"
   *  (usage safety net, design §21). Warn, never block: the level still opens and edits. */
  orphanLevelIds?: ReadonlySet<string>;
  /** Optional Game-profile strip, rendered under the Build group. */
  profileSlot?: ReactNode;
  onSelectBuildPlan: () => void;
  onSelectSection: (filename: string) => void;
  onSelectScreen: (screenId: string) => void;
  onNewScreen: () => void;
  onGenerateScreens: () => void;
  onSelectLevel: (levelId: string) => void;
  onNewLevel: () => void;
  onGenerateLevels: () => void;
  onEditThreeCs: () => void;
  onEditProgression: () => void;
}

export function DesignNav({
  hasBuildPlan,
  buildPlanActive,
  chip,
  footer,
  sections,
  activeFilename,
  changedSectionIds,
  changedScreenIds,
  screens,
  activeScreenId,
  generate,
  genError,
  levels,
  activeLevelId,
  profileSlot,
  onSelectBuildPlan,
  onSelectSection,
  onSelectScreen,
  onNewScreen,
  onGenerateScreens,
  onSelectLevel,
  onNewLevel,
  levelsGenerating,
  levelGenError,
  levelGenNote,
  levelGenProgress = null,
  onGenerateLevels,
  threeCsAuthored,
  activeThreeCs,
  onEditThreeCs,
  progressionAuthored,
  activeProgression,
  levelBadges,
  worldGroups,
  levelHealth,
  orphanLevelIds,
  onEditProgression,
}: DesignNavProps): ReactElement {
  const anyEdited = changedSectionIds.length > 0 || changedScreenIds.length > 0;

  const renderLevelLi = (l: LevelEntry): ReactElement => (
    <li key={l.id}>
      <button
        type="button"
        onClick={() => onSelectLevel(l.id)}
        className={navItemClass(activeLevelId === l.id)}
        title={l.name}
      >
        {levelBadges[l.id] && (
          <span className="w-5 shrink-0 whitespace-nowrap text-center font-mono text-[10px] tabular-nums text-crucible-accent-hover">
            {levelBadges[l.id]}
          </span>
        )}
        <span className="shrink-0 whitespace-nowrap text-[10px] uppercase opacity-60">
          {kindShortLabel(l.kind)}
        </span>
        <span className="min-w-0 flex-1 truncate">{l.name}</span>
        {orphanLevelIds?.has(l.id) && (
          <span
            title="Orphaned — not in the progression or any screen, so players can't reach it. It still opens and edits here."
            data-testid="design-level-orphan-chip"
            className="shrink-0 whitespace-nowrap rounded-full border border-amber-300/30 bg-amber-300/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-amber-200"
          >
            orphaned
          </span>
        )}
      </button>
    </li>
  );

  const worldHeaderLi = (key: string, label: string): ReactElement => (
    <li key={key} className="px-2 pb-0.5 pt-2 first:pt-0" data-testid="design-world-header">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-crucible-muted">
        {label}
      </span>
    </li>
  );

  const levelById = new Map(levels.map((l) => [l.id, l] as const));
  let levelListItems: ReactElement[];
  if (worldGroups) {
    levelListItems = [];
    worldGroups.groups.forEach((g, i) => {
      levelListItems.push(worldHeaderLi(`world-${g.setId}`, `${i + 1} · ${g.name}`));
      for (const id of g.levelIds) {
        const l = levelById.get(id);
        if (l) levelListItems.push(renderLevelLi(l));
      }
    });
    if (worldGroups.ungrouped.length > 0) {
      levelListItems.push(worldHeaderLi('world-unsorted', 'Unsorted'));
      for (const id of worldGroups.ungrouped) {
        const l = levelById.get(id);
        if (l) levelListItems.push(renderLevelLi(l));
      }
    }
  } else {
    levelListItems = [...levels]
      .sort((a, b) => {
        // Auto-sort by FLOW (progression order shown in the badge); levels not in
        // the progression sink to the bottom, then alphabetical for a stable order.
        const ra = levelBadges[a.id] ? parseInt(levelBadges[a.id], 10) : Number.POSITIVE_INFINITY;
        const rb = levelBadges[b.id] ? parseInt(levelBadges[b.id], 10) : Number.POSITIVE_INFINITY;
        return ra - rb || a.name.localeCompare(b.name);
      })
      .map(renderLevelLi);
  }

  return (
    <nav className="flex w-60 shrink-0 flex-col border-r border-crucible-border">
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {hasBuildPlan && (
          <>
            <div className="mb-1.5">
              <GroupHeading>Build</GroupHeading>
            </div>
            <button
              type="button"
              onClick={onSelectBuildPlan}
              data-testid="design-build-plan-nav"
              title="The approval artifact that starts a build — separate from your design docs"
              className={`mb-2 flex w-full flex-col gap-1.5 rounded-lg border p-2.5 text-left ${
                buildPlanActive
                  ? 'border-crucible-accent/50 bg-crucible-accent/10'
                  : 'border-crucible-border bg-white/[0.02] hover:border-crucible-accent/40'
              }`}
            >
              <span className="flex items-center gap-2 text-sm font-medium text-crucible-fg">
                <FileCheck2 size={14} className="text-crucible-accent-hover" aria-hidden="true" />
                Build Plan
              </span>
              <span
                data-testid="build-plan-chip"
                data-state={chip.state}
                className={`inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] ${CHIP_TONE_CLASS[chip.tone]}`}
              >
                {chip.pulse && (
                  <span
                    className="size-1.5 animate-pulse rounded-full bg-current"
                    aria-hidden="true"
                  />
                )}
                {chip.label}
              </span>
            </button>
          </>
        )}

        {profileSlot}

        <div className="mb-1 mt-4 first:mt-0">
          <GroupHeading>Design Docs</GroupHeading>
        </div>
        <ul className="space-y-0.5">
          {sections.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelectSection(s.filename)}
                className={navItemClass(activeFilename === s.filename)}
                title={s.title}
              >
                <span className="min-w-0 flex-1 truncate">{s.title}</span>
                {changedSectionIds.includes(s.id) && <EditedDot />}
              </button>
            </li>
          ))}
        </ul>

        <div className="mb-1 mt-4 flex items-center justify-between">
          <GroupHeading>Screens</GroupHeading>
          <button
            type="button"
            onClick={onGenerateScreens}
            disabled={generate.state === 'busy'}
            data-testid="design-generate-screens"
            data-state={generate.state}
            title={generate.title}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] hover:bg-crucible-bg-elevated disabled:opacity-60 ${
              generate.state === 'stale'
                ? 'border-crucible-accent/50 text-crucible-accent-hover'
                : 'border-crucible-border text-crucible-muted'
            }`}
          >
            {generate.state === 'stale' && (
              <span
                className="size-1 rounded-full bg-crucible-accent-hover"
                data-testid="design-generate-stale-dot"
                aria-hidden="true"
              />
            )}
            {generate.state === 'busy' && (
              <span className="size-1 animate-pulse rounded-full bg-amber-300" aria-hidden="true" />
            )}
            {generate.label}
          </button>
        </div>
        {genError && (
          <p className="mb-1 text-[10px] text-red-400" data-testid="design-generate-screens-error">
            {genError}
          </p>
        )}
        <ul className="space-y-0.5">
          {screens.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelectScreen(s.id)}
                className={navItemClass(activeScreenId === s.id)}
                title={s.name}
              >
                <span className="text-[10px] uppercase opacity-60">{s.surface}</span>
                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                {changedScreenIds.includes(s.id) && <EditedDot />}
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={onNewScreen}
              data-testid="design-new-screen"
              className="w-full rounded px-2 py-1 text-left text-sm text-crucible-accent-hover hover:bg-crucible-bg-elevated"
            >
              + New screen
            </button>
          </li>
        </ul>
        <div className="mb-1 mt-4 flex items-center justify-between">
          <GroupHeading>Levels</GroupHeading>
          <button
            type="button"
            onClick={onGenerateLevels}
            disabled={levelsGenerating}
            data-testid="design-generate-levels"
            title="Generate a course from the design"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-crucible-border px-1.5 py-0.5 text-[10px] text-crucible-muted hover:bg-crucible-bg-elevated disabled:opacity-60"
          >
            {levelsGenerating && (
              <span className="size-1 animate-pulse rounded-full bg-amber-300" aria-hidden="true" />
            )}
            {levelsGenerating ? 'Generating…' : 'Generate'}
          </button>
        </div>
        {levelGenProgress && (
          <p
            className="mb-1 text-[10px] text-crucible-muted"
            data-testid="design-generate-levels-progress"
          >
            {levelGenProgress}
          </p>
        )}
        {levelGenError && (
          <p className="mb-1 text-[10px] text-red-400" data-testid="design-generate-levels-error">
            {levelGenError}
          </p>
        )}
        {levelGenNote && (
          <p className="mb-1 text-[10px] text-amber-300" data-testid="design-generate-levels-note">
            {levelGenNote}
          </p>
        )}
        {levelHealth && levelHealth.errorLevels > 0 && (
          <p className="mb-1 text-[10px] text-red-400" data-testid="design-levels-error-count">
            ⚠ {levelHealth.errorLevels} level{levelHealth.errorLevels === 1 ? '' : 's'} fail the
            current 3Cs
          </p>
        )}
        {levelHealth && levelHealth.orphanLevels > 0 && (
          <p className="mb-1 text-[10px] text-amber-300" data-testid="design-levels-orphan-count">
            {levelHealth.orphanLevels} level{levelHealth.orphanLevels === 1 ? '' : 's'} reference
            missing Content
          </p>
        )}
        <ul className="space-y-0.5">
          <li>
            <button
              type="button"
              onClick={onEditThreeCs}
              className={navItemClass(activeThreeCs)}
              title="Feel / 3Cs — the measurable contract levels validate against"
            >
              <span className="text-[10px] uppercase opacity-60">feel</span>
              <span className="min-w-0 flex-1 truncate">3Cs profile</span>
              {!threeCsAuthored && (
                <span
                  title="Using the built-in default until saved"
                  className="size-1.5 shrink-0 rounded-full bg-amber-300"
                />
              )}
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={onEditProgression}
              className={navItemClass(activeProgression)}
              title="Progression — which level is level 1, and how the rest unlock"
            >
              <span className="text-[10px] uppercase opacity-60">flow</span>
              <span className="min-w-0 flex-1 truncate">Progression</span>
              {!progressionAuthored && (
                <span
                  title="Not set up yet"
                  className="size-1.5 shrink-0 rounded-full bg-amber-300"
                />
              )}
            </button>
          </li>
          {levelListItems}
          <li>
            <button
              type="button"
              onClick={onNewLevel}
              data-testid="design-new-level"
              className="w-full rounded px-2 py-1 text-left text-sm text-crucible-accent-hover hover:bg-crucible-bg-elevated"
            >
              + New level
            </button>
          </li>
        </ul>

        {anyEdited && (
          <p
            className="mt-3 flex items-center gap-1.5 px-2 text-[10px] text-crucible-muted"
            data-testid="design-edited-legend"
          >
            <span className="size-1.5 shrink-0 rounded-full bg-amber-300" aria-hidden="true" />
            edited since last build
          </p>
        )}
      </div>

      {/* Pinned footer — exactly one quiet line per state. The send CTA lives
          in the right-rail Build card, never here. */}
      <div className="shrink-0 border-t border-crucible-border p-3">
        {footer.clickable ? (
          <button
            type="button"
            onClick={onSelectBuildPlan}
            data-testid="design-nav-footer"
            data-state={footer.kind}
            className={`flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-dashed px-2.5 py-1.5 text-left text-xs hover:bg-crucible-bg-elevated ${
              footer.tone === 'amber' ? 'border-amber-300/35' : 'border-white/20'
            } ${FOOTER_TONE_CLASS[footer.tone]}`}
          >
            <span className="min-w-0 truncate">{footer.text}</span>
            <span aria-hidden="true">›</span>
          </button>
        ) : (
          <div
            data-testid="design-nav-footer"
            data-state={footer.kind}
            className={`flex items-center gap-2 px-2.5 py-1.5 text-xs ${FOOTER_TONE_CLASS[footer.tone]}`}
          >
            {footer.kind === 'running' && (
              <span
                className="size-1.5 shrink-0 animate-pulse rounded-full bg-amber-300"
                aria-hidden="true"
              />
            )}
            {footer.kind === 'pending' && (
              <span className="size-1.5 shrink-0 rounded-full bg-amber-300" aria-hidden="true" />
            )}
            {footer.kind === 'in-sync' && (
              <Check size={13} className="shrink-0 text-emerald-300" aria-hidden="true" />
            )}
            <span className="min-w-0 truncate">{footer.text}</span>
          </div>
        )}
      </div>
    </nav>
  );
}
